<template>
  <div class="media-grid-wrap">
    <div class="media-layout">
      <!-- ============ 左侧分组栏（照图1：全部高亮蓝/各分组含count/新建分组/底部回收站入口） ============ -->
      <div class="group-sidebar">
        <div class="group-item" :class="{ active: !recycleBin && activeGroup === '' }" @click="selectGroup('')">
          <a-icon type="appstore" />
          <span class="group-name">全部</span>
        </div>
        <div
          v-for="g in groups"
          :key="g.id"
          class="group-item"
          :class="{ active: !recycleBin && String(activeGroup) === String(g.id) }"
          @click="selectGroup(g.id)">
          <a-icon type="folder" />
          <span class="group-name" :title="g.name">{{ g.name }}</span>
          <span class="group-count">{{ g.count }}</span>
          <a-icon v-if="!selectable" type="close" class="group-del" @click.stop="removeGroup(g)" />
        </div>
        <div class="group-item add" @click="groupModal.visible = true">
          <a-icon type="folder-add" />
          <span class="group-name">新建分组</span>
        </div>
        <div class="group-item recycle-entry" :class="{ active: recycleBin }" @click="selectRecycleBin">
          <a-icon type="delete" />
          <span class="group-name">回收站</span>
        </div>
      </div>
      <!-- ============ 右侧主区：顶栏 + 缩略图网格 + 分页 ============ -->
      <div class="main-area">
        <div class="media-toolbar">
          <a-input-search
            v-model="keyword"
            placeholder="请输入图片名称"
            style="width: 240px"
            allow-clear
            @search="onSearch" />
          <template v-if="!recycleBin">
            <a-upload
              :multiple="true"
              :show-upload-list="false"
              accept="image/*"
              :before-upload="handleUpload">
              <a-button type="primary" icon="upload" :loading="uploading">上传图片</a-button>
            </a-upload>
            <a-button
              v-if="!selectable"
              icon="drag"
              :disabled="!selectedIds.length"
              @click="openMoveModal">移动图片</a-button>
            <a-button
              v-if="!selectable"
              icon="delete"
              :disabled="!selectedIds.length"
              @click="batchRemove">批量删除</a-button>
          </template>
          <a-button
            v-if="recycleBin"
            type="danger"
            icon="clear"
            :disabled="!total"
            @click="emptyRecycleBin">清空回收站</a-button>
        </div>
        <a-spin :spinning="loading">
          <div v-if="rows.length" class="pic-grid">
            <div
              v-for="item in rows"
              :key="item.id"
              class="pic-card"
              :class="{ selected: selectable && selectedIds.indexOf(item.id) > -1 }">
              <div class="pic-thumb" @click="onPicClick(item)">
                <img :src="imgUrl(item.url)" :alt="item.name" />
                <a-icon
                  v-if="!selectable"
                  type="close-circle"
                  class="pic-del"
                  @click.stop="removeOne(item)" />
                <span v-if="selectable && selectedIds.indexOf(item.id) > -1" class="pic-check">
                  <a-icon type="check" />
                </span>
              </div>
              <div class="pic-name" :title="item.name">{{ item.name }}</div>
              <div v-if="recycleBin" class="pic-actions">
                <a @click="restoreOne(item)">恢复</a>
                <a-divider type="vertical" />
                <a @click="purgeOne(item)">彻底删除</a>
              </div>
            </div>
          </div>
          <a-empty v-else :description="recycleBin ? '回收站为空' : '暂无图片，请上传'" style="margin-top: 60px" />
        </a-spin>
        <div class="media-pagination">
          <a-pagination
            :current="page"
            :page-size="pageSize"
            :total="total"
            :show-total="t => `共 ${t} 张`"
            @change="onPageChange" />
        </div>
      </div>
    </div>
    <!-- ============ 新建分组弹窗 ============ -->
    <a-modal
      :visible="groupModal.visible"
      title="新建分组"
      ok-text="确定"
      cancel-text="取消"
      :confirm-loading="groupModal.saving"
      @ok="createGroup"
      @cancel="groupModal.visible = false">
      <a-input v-model="groupModal.name" placeholder="请输入分组名称" :maxLength="20" @pressEnter="createGroup" />
    </a-modal>
    <!-- ============ 移动图片弹窗 ============ -->
    <a-modal
      :visible="moveModal.visible"
      title="移动图片"
      ok-text="确定"
      cancel-text="取消"
      :confirm-loading="moveModal.saving"
      @ok="moveSelected"
      @cancel="moveModal.visible = false">
      <a-radio-group v-model="moveModal.targetGroupId" class="move-group-list">
        <a-radio :value="0">未分组</a-radio>
        <a-radio v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</a-radio>
      </a-radio-group>
    </a-modal>
  </div>
</template>

<script>
import { getFileAccessHttpUrl } from '@/api/manage'
import { mediaList, mediaRegister, mediaMove, mediaRemove, mediaRestore, mediaPurge, mediaGroupAdd, mediaGroupDelete, mediaUpload } from '@/api/plugin/dining'

/**
 * 素材库网格核心组件（素材管理页与 MediaPickerModal 弹窗同源复用，照云贝素材管理图1 布局）
 * props：
 *   selectable  true=选择模式（选择器弹窗）：点击图片选中/单选直接确认；false=管理模式（素材页）：点击图片 emit preview
 *   multiple    选择模式下是否多选（单选时点击图片直接 emit pick）
 *   limit       多选上限（0 = 不限）
 * events：
 *   preview(item)         管理模式点击图片（父级负责大图预览）
 *   pick(item)            选择模式单选点击（父级直接确认返回）
 *   selection-change(items) 多选选中集变化（父级确认按钮取值）
 * 暴露方法（ref 调用）：
 *   reload()         重置到第一页刷新
 *   clearSelection() 清空选中
 */
export default {
  name: 'MediaGrid',
  props: {
    selectable: { type: Boolean, default: false },
    multiple: { type: Boolean, default: true },
    limit: { type: Number, default: 0 }
  },
  data() {
    return {
      groups: [],
      activeGroup: '',
      recycleBin: false,
      keyword: '',
      rows: [],
      page: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      uploading: false,
      pendingUploads: 0,
      selectedIds: [],
      groupModal: { visible: false, name: '', saving: false },
      moveModal: { visible: false, targetGroupId: 0, saving: false }
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    // 图片回显地址（素材库存 upload 返回的 savePath，统一拼静态资源前缀；完整 http 直接用）
    imgUrl(path) {
      if (!path) {
        return ''
      }
      if (path.indexOf('http') === 0) {
        return path
      }
      return getFileAccessHttpUrl('systemConfig/static/' + path)
    },
    // ===== 列表加载（列表+分组数一起返回） =====
    loadList() {
      const that = this
      this.loading = true
      const params = {
        groupId: this.activeGroup,
        keyword: (this.keyword || '').trim(),
        recycleBin: this.recycleBin,
        page: this.page,
        pageSize: this.pageSize
      }
      mediaList(params).then(res => {
        if (res.code === 200 && res.data) {
          that.rows = res.data.rows || []
          that.total = res.data.total || 0
          that.groups = res.data.groups || []
        } else {
          that.$message.warning((res && res.message) || '素材列表加载失败')
        }
      }).catch(() => {}).finally(() => {
        that.loading = false
      })
    },
    reload() {
      this.page = 1
      this.clearSelection()
      this.loadList()
    },
    onSearch() {
      this.page = 1
      this.loadList()
    },
    onPageChange(page) {
      this.page = page
      this.loadList()
    },
    // ===== 分组切换 =====
    selectGroup(groupId) {
      if (this.recycleBin || String(this.activeGroup) !== String(groupId)) {
        this.recycleBin = false
        this.activeGroup = groupId
        this.page = 1
        this.clearSelection()
        this.loadList()
      }
    },
    selectRecycleBin() {
      if (!this.recycleBin) {
        this.recycleBin = true
        this.page = 1
        this.clearSelection()
        this.loadList()
      }
    },
    // ===== 新建分组 =====
    createGroup() {
      const name = (this.groupModal.name || '').trim()
      if (!name) {
        this.$message.warning('请输入分组名称')
        return
      }
      this.groupModal.saving = true
      mediaGroupAdd({ name: name }).then(res => {
        if (res.code === 200) {
          this.$message.success('分组创建成功')
          this.groupModal.visible = false
          this.groupModal.name = ''
          this.loadList()
        } else {
          this.$message.warning((res && res.message) || '分组创建失败')
        }
      }).catch(() => {}).finally(() => {
        this.groupModal.saving = false
      })
    },
    // ===== 删除分组（组内素材移到未分组） =====
    removeGroup(g) {
      const that = this
      this.$confirm({
        title: '删除分组',
        content: `确定删除分组「${g.name}」吗？组内 ${g.count} 张图片将移到未分组`,
        onOk() {
          return mediaGroupDelete(g.id).then(res => {
            if (res.code === 200) {
              that.$message.success('分组已删除')
              if (String(that.activeGroup) === String(g.id)) {
                that.activeGroup = ''
              }
              that.loadList()
            } else {
              that.$message.warning((res && res.message) || '分组删除失败')
            }
          })
        }
      })
    },
    // ===== 上传（a-upload beforeUpload 拦截手动上传：主工程 /systemConfig/upload + 登记 /media） =====
    handleUpload(file) {
      this.uploadOne(file)
      return false
    },
    uploadOne(file) {
      const that = this
      if (!file.type || file.type.indexOf('image') !== 0) {
        this.$message.warning('只能上传图片文件')
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      formData.append('biz', 'material')
      this.pendingUploads++
      this.uploading = true
      mediaUpload(formData).then(res => {
        if (res.code === 200 && res.data) {
          return mediaRegister({ url: res.data, name: file.name, groupId: this.activeGroup === '' ? 0 : Number(this.activeGroup) }).then(() => {
            that.$message.success(`${file.name} 上传成功`)
          }).catch(() => {})
        } else {
          that.$message.warning((res && res.data) || '上传失败')
        }
      }).catch(() => {
        that.$message.warning(`${file.name} 上传失败`)
      }).finally(() => {
        that.pendingUploads--
        if (that.pendingUploads <= 0) {
          that.pendingUploads = 0
          that.uploading = false
          that.loadList()
        }
      })
    },
    // ===== 图片点击：管理模式预览 / 选择模式选中（单选直接确认） =====
    onPicClick(item) {
      if (!this.selectable) {
        this.$emit('preview', item)
        return
      }
      if (!this.multiple) {
        this.$emit('pick', item)
        return
      }
      const idx = this.selectedIds.indexOf(item.id)
      if (idx > -1) {
        this.selectedIds.splice(idx, 1)
      } else {
        if (this.limit > 0 && this.selectedIds.length >= this.limit) {
          this.$message.warning(`最多选择 ${this.limit} 张`)
          return
        }
        this.selectedIds.push(item.id)
      }
      this.emitSelection()
    },
    emitSelection() {
      const items = this.rows.filter(r => this.selectedIds.indexOf(r.id) > -1)
      this.$emit('selection-change', items)
    },
    clearSelection() {
      this.selectedIds = []
      this.emitSelection()
    },
    // ===== 管理操作：单删（软删进回收站）/ 批量删 / 移动 / 恢复 / 彻底删 / 清空回收站 =====
    removeOne(item) {
      const that = this
      this.$confirm({
        title: '删除图片',
        content: `确定删除「${item.name}」吗？删除后进入回收站`,
        onOk() {
          return mediaRemove(item.id).then(res => {
            if (res.code === 200) {
              that.$message.success('已移入回收站')
              that.loadList()
            } else {
              that.$message.warning((res && res.message) || '删除失败')
            }
          })
        }
      })
    },
    batchRemove() {
      if (!this.selectedIds.length) {
        return
      }
      const that = this
      const count = this.selectedIds.length
      this.$confirm({
        title: '批量删除',
        content: `确定删除选中的 ${count} 张图片吗？删除后进入回收站`,
        onOk() {
          return mediaRemove(that.selectedIds.join(',')).then(res => {
            if (res.code === 200) {
              that.$message.success('已移入回收站')
              that.clearSelection()
              that.loadList()
            } else {
              that.$message.warning((res && res.message) || '删除失败')
            }
          })
        }
      })
    },
    openMoveModal() {
      if (!this.selectedIds.length) {
        return
      }
      this.moveModal.targetGroupId = this.activeGroup === '' ? 0 : Number(this.activeGroup)
      this.moveModal.visible = true
    },
    moveSelected() {
      const that = this
      this.moveModal.saving = true
      mediaMove({ ids: this.selectedIds, groupId: this.moveModal.targetGroupId }).then(res => {
        if (res.code === 200) {
          that.$message.success('移动成功')
          that.moveModal.visible = false
          that.clearSelection()
          that.loadList()
        } else {
          that.$message.warning((res && res.message) || '移动失败')
        }
      }).catch(() => {}).finally(() => {
        that.moveModal.saving = false
      })
    },
    restoreOne(item) {
      const that = this
      mediaRestore(item.id).then(res => {
        if (res.code === 200) {
          that.$message.success('已恢复')
          that.loadList()
        } else {
          that.$message.warning((res && res.message) || '恢复失败')
        }
      }).catch(() => {})
    },
    purgeOne(item) {
      const that = this
      this.$confirm({
        title: '彻底删除',
        content: `确定彻底删除「${item.name}」吗？删除后不可恢复`,
        okType: 'danger',
        onOk() {
          return mediaPurge(item.id).then(res => {
            if (res.code === 200) {
              that.$message.success('已彻底删除')
              that.loadList()
            } else {
              that.$message.warning((res && res.message) || '删除失败')
            }
          })
        }
      })
    },
    emptyRecycleBin() {
      const that = this
      this.$confirm({
        title: '清空回收站',
        content: '回收站内所有图片将被彻底删除，不可恢复，确定继续吗？',
        okType: 'danger',
        onOk() {
          return mediaPurge('').then(res => {
            if (res.code === 200) {
              that.$message.success('回收站已清空')
              that.loadList()
            } else {
              that.$message.warning((res && res.message) || '操作失败')
            }
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.media-layout {
  display: flex;
  min-height: 480px;
}
/* 左侧分组栏 */
.group-sidebar {
  width: 180px;
  flex-shrink: 0;
  border-right: 1px solid #e8e8e8;
  padding-right: 12px;
  margin-right: 16px;
}
.group-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  color: #333;
  margin-bottom: 4px;
}
.group-item:hover {
  background: #f5f5f5;
}
.group-item.active {
  background: #1890ff;
  color: #fff;
}
.group-item.active .group-count {
  color: #fff;
}
.group-name {
  flex: 1;
  margin-left: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group-count {
  color: #999;
  font-size: 12px;
  margin-left: 4px;
}
.group-item.active .group-del,
.group-item .group-del {
  display: none;
  color: #999;
  margin-left: 4px;
}
.group-item:hover .group-del {
  display: inline-block;
}
.group-item .group-del:hover {
  color: #f5222d;
}
.group-item.add {
  color: #1890ff;
}
.recycle-entry {
  margin-top: 16px;
  border-top: 1px dashed #e8e8e8;
  padding-top: 12px;
}
/* 顶栏 */
.media-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.main-area {
  flex: 1;
  min-width: 0;
}
/* 缩略图网格（方形 cover + 文件名 + hover x + 选中蓝框） */
.pic-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  min-height: 200px;
}
.pic-card {
  width: 124px;
}
.pic-thumb {
  position: relative;
  width: 124px;
  height: 124px;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #fafafa;
}
.pic-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pic-thumb:hover {
  border-color: #91d5ff;
}
.pic-card.selected .pic-thumb {
  border-color: #1890ff;
}
.pic-del {
  display: none;
  position: absolute;
  top: 4px;
  right: 4px;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 50%;
  padding: 2px;
  font-size: 12px;
}
.pic-thumb:hover .pic-del {
  display: block;
}
.pic-del:hover {
  background: #f5222d;
}
.pic-check {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  background: #1890ff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
}
.pic-name {
  font-size: 12px;
  color: #333;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}
.pic-actions {
  text-align: center;
  font-size: 12px;
  margin-top: 2px;
}
.move-group-list {
  display: flex;
  flex-direction: column;
}
.move-group-list /deep/ .ant-radio-wrapper {
  margin-bottom: 8px;
}
.media-pagination {
  margin-top: 16px;
  text-align: right;
}
</style>
